import { useMemo, type ReactNode } from 'react';
import { PieChart, Pie, Legend, ResponsiveContainer, Label, Text, type TextProps } from 'recharts';
import { useIntl } from 'react-intl';
import theme from 'config/theme';
import { Hidden } from './Grid';
import s from './DonutChart.module.scss';

interface IDonutChartProject {
  price: number | null;
  budget: number | null;
}

/** What recharts passes to the content of a Pie label. */
interface IPieLabelContentProps {
  viewBox: { cx: number; cy: number };
  width?: number;
}

interface IDonutChartProps {
  height?: number;
  thickness?: number;
  legendHeight?: number;
  project: IDonutChartProject;
  labelInsideChart?: ReactNode;
  hideLegendOnMobile?: boolean;
}

export default function DonutChart({
  height = 310,
  thickness = 50,
  legendHeight = 30,
  project,
  labelInsideChart,
  hideLegendOnMobile = true,
}: IDonutChartProps) {
  const { formatMessage: t } = useIntl();
  const labelPadding = 0;
  const radius = height / 2 - legendHeight - labelPadding * 2;
  const isLargeScreen = window.innerWidth >= 768;
  const labelFontSize = window.innerWidth <= 992 ? '0.7rem' : '1.15rem';

  const data = useMemo(
    () => [
      {
        name: t({ id: 'dashboard.budget_shortage_chart.shortage_label' }),
        // `price - budget` with null as 0, as the subtraction coerced it.
        value: Math.max(0, Number(project.price) - Number(project.budget)),
        fill: theme.primaryColor,
      },
      {
        name: t({ id: 'dashboard.budget_shortage_chart.budget_label' }),
        value: project.budget,
        fill: theme.yellowColor,
      },
    ],
    [project.budget, project.price, t],
  );

  const ChartLegend = (
    <Legend
      align="center"
      verticalAlign="bottom"
      iconType="circle"
      iconSize={24}
      height={legendHeight}
    />
  );

  return (
    <ResponsiveContainer width="100%" height={height} className={s.donutChart}>
      <PieChart>
        <Pie dataKey="value" data={data} outerRadius={radius} innerRadius={radius - thickness}>
          {labelInsideChart ? (
            <Label
              width={110}
              content={(props: IPieLabelContentProps) => {
                const {
                  viewBox: { cx, cy },
                  width,
                } = props;
                const positioningProps = {
                  x: cx,
                  y: cy + 5,
                  width,
                  textAnchor: 'middle',
                  verticalAnchor: 'middle',
                  fill: 'black',
                  fontSize: labelFontSize,
                  fontWeight: 'bold',
                } satisfies TextProps;

                // const contentPositioningProps = {
                //   x: cx - (isLargeScreen ? 25 : 15),
                //   y: cy - (isLargeScreen ? 65 : 40),
                //   textAnchor: 'middle',
                //   verticalAnchor: 'middle',
                // };

                return <Text {...positioningProps}>{labelInsideChart}</Text>;
              }}
            />
          ) : null}
        </Pie>

        {!isLargeScreen && hideLegendOnMobile ? (
          <Hidden xs sm>
            {ChartLegend}
          </Hidden>
        ) : (
          ChartLegend
        )}
      </PieChart>
    </ResponsiveContainer>
  );
}
